import { useEffect, useMemo, useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"
import { Check, ChevronRight, Pencil, Search, ShieldAlert } from "lucide-react"
import { api } from "@/lib/api"
import { toast } from "@/components/portal/Toast"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import { EmailDetailPane } from "../EmailsList"
import { ComposeModal } from "./ComposeModal"
import { cleanSender, fmtDate, fmtZeitpunkt } from "./format"
import { HAUPT, postfachName, postfachQuery, usePostfachWahl } from "./postfach"
import { fetchEmails } from "./data"
import { PostfachTreffer } from "./PostfachSuche"
import { istSpamOrdner } from "./ActionBar"
import { Anhang, AuswahlLeiste, ListenAnsicht, OrdnerSpalte, UngelesenPunkt, Waehlbar, ablageZiele, useOrdner, useSammelaktion, type Liste } from "./OrdnerAnsichten"
import { useGelesen, zeilenKlasse } from "./gelesen"

// ── Wahl je Person: neues Postfach oder bisherige Ansicht ────────────────────

const MODUS = "ev2_modus"
const MODUS_EREIGNIS = "ev2-modus"
export type PostfachModus = "karte" | "liste"

function modusLesen(): PostfachModus {
  try { return localStorage.getItem(MODUS) === "liste" ? "liste" : "karte" } catch { return "karte" }
}

/** Neues Postfach (Standard) oder die bisherige Liste — gilt je Browser und
 *  gleicht alle Stellen an, die es lesen. */
export function usePostfachModus(): [PostfachModus, (m: PostfachModus) => void] {
  const [m, setM] = useState<PostfachModus>(modusLesen)
  useEffect(() => {
    const neu = () => setM(modusLesen())
    window.addEventListener(MODUS_EREIGNIS, neu)
    window.addEventListener("storage", neu)
    return () => { window.removeEventListener(MODUS_EREIGNIS, neu); window.removeEventListener("storage", neu) }
  }, [])
  const setzen = (wert: PostfachModus) => {
    try { localStorage.setItem(MODUS, wert) } catch { /* privat */ }
    setM(wert)
    window.dispatchEvent(new Event(MODUS_EREIGNIS))
  }
  return [m, setzen]
}

// ── Daten ────────────────────────────────────────────────────────────────────

export type ToepfeZeile = {
  id: number
  sender?: string | null
  sender_email?: string | null
  subject?: string | null
  received_at?: string | null
  zustaendig?: string | null
  topf: "klick" | "hand"
  kurz: string
  has_attachments?: boolean | null
  imap_folder?: string | null
  topf_grund?: string | null
  gelesen?: boolean
  vorschau?: string
}

export type AutomatikGruppe = {
  regel: string
  label: string
  beschreibung: string
  ziel: string
  anzahl: number
  rueckgeholt: number
  mails: { id: number; absender: string; betreff: string; ordner: string; rueckgeholt: boolean }[]
}

export type KenntnisZeile = {
  id: number
  sender?: string | null
  sender_email?: string | null
  subject?: string | null
  received_at?: string | null
  has_attachments?: boolean
  imap_folder?: string | null
  art: "info" | "werbung" | "phishing" | "ohne_plan"
  grund: string
  gelesen?: boolean
  vorschau?: string
}

export type Toepfe = {
  kenntnis?: KenntnisZeile[]
  klick: ToepfeZeile[]
  hand: ToepfeZeile[]
  automatik: AutomatikGruppe[]
  automatik_anzahl: number
  erledigt: { id: number; absender: string; betreff: string; um?: string | null; benutzer?: string | null }[]
  ich?: string | null
}

/** Der Schlüssel beginnt mit „emails“: jede Aktion, die die Mail-Liste neu
 *  lädt, lädt damit auch die Töpfe neu. */
export function useToepfe(tenant: string, postfach: string) {
  return useQuery<Toepfe>({
    queryKey: ["emails", "toepfe", tenant, postfach],
    queryFn: async () => {
      const r = await fetch(`/api/v1/emails/toepfe?tenant=${encodeURIComponent(tenant)}${postfachQuery(postfach)}`,
        { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return await r.json() as Toepfe
    },
    enabled: !!tenant,
    refetchInterval: 60_000,
  })
}

/** Name und Adresse des Absenders — am Namen allein erkennt man oft nicht,
 *  wer schreibt (Paul 03.11.2026). Die Adresse nur, wenn sie etwas hinzufügt. */
function Absender({ zeile }: { zeile: { sender?: string | null; sender_email?: string | null } }) {
  const name = cleanSender(zeile.sender)
  const adresse = (zeile.sender_email || "").trim()
  return (
    <span className="pk-absender" data-pii>
      <b>{name || adresse || "—"}</b>
      {name && adresse && name.toLowerCase() !== adresse.toLowerCase() && <span className="pk-absender-adresse">{adresse}</span>}
    </span>
  )
}

function uhrzeit(iso?: string | null): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  const heute = new Date()
  if (d.toDateString() === heute.toDateString()) {
    return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
  }
  return fmtDate(iso)
}

// ── Seite ────────────────────────────────────────────────────────────────────

/** Welche Mail die Karte zeigt. Ohne Wahl die erste offene Mail — „Ein Klick“
 *  vor „Braucht dich“. Eine Wahl, die in keinem Topf mehr liegt (erledigt,
 *  verschoben), fällt ebenfalls auf die erste offene zurück — außer sie kam
 *  per Klick aus dem Verlauf der Karte (#171, Marlene 29.10.2026: „öffnet die
 *  neueste Mail statt der angeklickten“). */
/**
 * `?open=<id>` aus der Adresse — Banner „Neue E-Mail“ oben rechts (Paul
 * 03.11.2026): Stand man schon im Postfach, änderte der Klick nur die Adresse,
 * die Karte las `open` aber nur beim Laden und blieb stehen. Zeigt die Karte
 * die Mail schon, passiert nichts (eigene Klicks schreiben `open` mit).
 */
export function useOpenAusAdresse(sel: number | null, oeffnen: (id: number) => void) {
  const [params] = useSearchParams()
  const open = Number(params.get("open")) || null
  useEffect(() => {
    if (open && open !== sel) oeffnen(open)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
}

export function aktiveMail(a: {
  sel: number | null; ausVerlauf: number | null; liste: boolean; sucht: boolean
  geladen: boolean; inTopf: boolean; ersteOffene: number | null
}): number | null {
  if (a.liste) return a.sel
  if (a.sel && (a.sel === a.ausVerlauf || a.inTopf || !a.geladen || a.sucht)) return a.sel
  return a.ersteOffene
}

/**
 * Das neue Postfach (Paul 28.10.2026): links die Töpfe, rechts eine Mail als
 * Karte. Ziel: so wenig Klicks wie möglich — was der Agent sicher kann, ist
 * ein Klick; was er nicht weiß, fragt er; was eine Regel erledigt hat, lässt
 * sich prüfen und zurückholen.
 */
export function PostfachKarte({ onBisherige }: { onBisherige: () => void }) {
  const [tenant, setTenant] = useTenantFilter()
  const pf = usePostfachWahl(tenant)
  const [params, setParams] = useSearchParams()
  const [sel, setSel] = useState<number | null>(() => Number(params.get("open")) || null)
  // Aus dem Verlauf der Karte (#171) oder per Adresse (Banner) geöffnet:
  // gilt, obwohl in keinem Topf.
  const [ausVerlauf, setAusVerlauf] = useState<number | null>(() => Number(params.get("open")) || null)
  const [ansicht, setAnsicht] = useState<"karte" | "automatik">("karte")
  const [composeOpen, setComposeOpen] = useState(false)
  const [suche, setSuche] = useState("")
  // „Ordner & Ansichten“: statt der Töpfe eine Liste (Gesendet, Ordner, Mir zugewiesen …)
  const [liste, setListe] = useState<Liste | null>(null)
  // Mehrfachauswahl und Ziehen (Paul 28.10.2026: „vorher hatten wir Drag & Drop“)
  const [auswahl, setAuswahl] = useState<number[]>([])
  const [zieht, setZieht] = useState(false)
  const haken = (id: number) => setAuswahl((a) => a.includes(id) ? a.filter((x) => x !== id) : [...a, id])
  const wahl = { auswahl, onHaken: haken, onZiehen: setZieht }
  const sammelaktion = useSammelaktion(() => { setAuswahl([]); setZieht(false) })
  const gelesenSetzen = useGelesen()
  const listeWaehlen = (l: Liste | null) => { setListe(l); setSel(null); setAuswahl([]) }
  const qc = useQueryClient()

  const tenantsDd = useQuery({
    queryKey: ["tenants-dropdown"],
    queryFn: async () => (await api.GET("/api/v1/tenants", {})).data,
    staleTime: 1000 * 60 * 5,
  })
  // Wie die bisherige Liste: ohne Betrieb mischt sich alles — letzte Wahl
  // oder der erste Betrieb.
  useEffect(() => {
    if (tenant) { try { localStorage.setItem("emails-last-tenant", tenant) } catch { /* privat */ } return }
    const items = tenantsDd.data?.items
    if (!items?.length) return
    let last: string | null = null
    try { last = localStorage.getItem("emails-last-tenant") } catch { /* privat */ }
    const pick = (last && items.find((t) => t.slug === last)?.slug) || items[0].slug
    if (pick) setTenant(pick)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, tenantsDd.data])

  // Suche bleibt im neuen Postfach (Paul 28.10.2026): links stehen dann die
  // Treffer aus ALLEN Ordnern statt der Töpfe, rechts öffnet sich die Mail als
  // Karte. Darunter wie gewohnt ältere Mails live aus dem Postfach.
  const [begriff, setBegriff] = useState("")
  useEffect(() => {
    const t = setTimeout(() => setBegriff(suche.trim()), 300)
    return () => clearTimeout(t)
  }, [suche])
  const sucht = begriff.length >= 2
  const trefferQ = useQuery({
    queryKey: ["emails", "toepfe-suche", tenant, pf.wahl, begriff],
    queryFn: () => fetchEmails(tenant, begriff, undefined, pf.wahl || undefined, 100),
    enabled: !!tenant && sucht,
  })
  const sucheStarten = (ev: React.FormEvent) => { ev.preventDefault(); setBegriff(suche.trim()) }

  const q = useToepfe(tenant, pf.wahl)
  const [erledigtZu, erledigtUmschalten] = useZugeklappt("erledigt")
  const ordnerQ = useOrdner(tenant, pf.wahl)
  const klick = q.data?.klick || []
  const hand = q.data?.hand || []
  const offen = useMemo(() => [...klick, ...hand], [klick, hand])
  const gewaehlt = offen.find((z) => z.id === sel)
  const erledigtGewaehlt = (q.data?.erledigt || []).some((z) => z.id === sel)
    || (q.data?.kenntnis || []).some((z) => z.id === sel)
  const aktiv = aktiveMail({
    sel, ausVerlauf, liste: !!liste, sucht, geladen: !!q.data,
    inTopf: !!gewaehlt || erledigtGewaehlt, ersteOffene: offen[0]?.id ?? null,
  })

  const oeffnen = (id: number) => {
    setSel(id)
    setAusVerlauf(null)
    setAnsicht("karte")
    const p = new URLSearchParams(params)
    p.set("open", String(id))
    setParams(p, { replace: true })
  }
  // #171: Ein Klick im Verlauf der Karte öffnet genau diese Mail — auch wenn
  // sie in keinem Topf liegt (z. B. die gesendete Terminbestätigung davor).
  const ausVerlaufOeffnen = (id: number) => { oeffnen(id); setAusVerlauf(id) }
  useOpenAusAdresse(sel, ausVerlaufOeffnen)
  const naechste = () => {
    const i = offen.findIndex((z) => z.id === aktiv)
    const nach = offen.filter((z) => z.id !== aktiv)
    const n = nach[Math.max(0, Math.min(i, nach.length - 1))]
    setSel(n ? n.id : null)
  }


  const n = offen.length
  return (
    <div className="pk-seite">
      <header className="pk-kopfleiste">
        <div className="pk-titel">
          <h1>Postfach</h1>
          <select value={tenant} onChange={(e) => { setTenant(e.target.value); listeWaehlen(null) }} aria-label="Betrieb">
            {tenantsDd.data?.items?.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
          </select>
          {pf.weitere.length > 0 && (
            <select value={pf.wahl} onChange={(e) => { pf.setWahl(e.target.value); listeWaehlen(null) }} aria-label="Postfach">
              <option value="">Alle Postfächer</option>
              {pf.haupt && <option value={HAUPT}>{postfachName(pf.haupt)}</option>}
              {pf.weitere.map((p) => <option key={p.adresse} value={p.adresse}>{postfachName(p)}</option>)}
            </select>
          )}
        </div>
        <form className="pk-suche" role="search" onSubmit={sucheStarten}>
          <Search className="w-4 h-4" aria-hidden />
          <label htmlFor="pk-suche" className="pk-unsichtbar">Alle Mails durchsuchen</label>
          <input id="pk-suche" type="search" value={suche} onChange={(e) => setSuche(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") setSuche("") }}
            placeholder="Alle Mails durchsuchen: Kunde, Projekt, Betreff" />
        </form>
        <button type="button" className="pk-knopf-leise" onClick={onBisherige} data-testid="bisherige-ansicht"
          title="Zur bisherigen Ansicht (gilt nur für dich, jederzeit zurück)">Bisherige Ansicht</button>
        <button type="button" className="pk-knopf" onClick={() => setComposeOpen(true)}>
          <Pencil className="w-4 h-4" aria-hidden /> Neue Mail
        </button>
      </header>

      <div className="pk-inhalt">
        <OrdnerSpalte tenant={tenant} postfach={pf.wahl} aktiv={sucht ? null : liste} auswahl={auswahl} zieht={zieht}
          onWahl={(l) => { setSuche(""); listeWaehlen(l) }}
          onVerschieben={(ordner) => sammelaktion(auswahl, "move", ordner)}
          onAbgelegt={() => { setAuswahl([]); setZieht(false) }} />
        <aside className="pk-toepfe" aria-label={sucht ? "Suchergebnisse" : liste ? liste.titel : "Offene Mails"}>
          <AuswahlLeiste ids={auswahl} ziele={ablageZiele(ordnerQ.data || [])}
            onAktion={(action, folder) => sammelaktion(auswahl, action, folder || "")} onAufheben={() => setAuswahl([])}
            onGelesen={(g) => { gelesenSetzen(auswahl, g); setAuswahl([]) }} />
          {!sucht && liste ? (
            <ListenAnsicht tenant={tenant} postfach={pf.wahl} liste={liste} aktiv={ansicht === "karte" ? aktiv : null}
              onOeffnen={oeffnen} onZurueck={() => listeWaehlen(null)} wahl={wahl} />
          ) : sucht ? (
            <div className="pk-topf" data-testid="suchtreffer">
              <h2 className="pk-topf-titel leise">
                <span>Treffer für „{begriff}“</span>
                <span>{trefferQ.data ? trefferQ.data.items.length : "…"}</span>
              </h2>
              <button type="button" className="pk-link" style={{ alignSelf: "flex-start", marginBottom: 4 }}
                onClick={() => setSuche("")}>Suche beenden</button>
              {trefferQ.isError && <p className="pk-fehler">Die Suche ist fehlgeschlagen. Bitte noch einmal versuchen.</p>}
              {(trefferQ.data?.items || []).map((z) => (
                <Waehlbar key={z.id} id={z.id} ordner={z.imap_folder} gewaehlt={auswahl.includes(z.id)} auswahl={auswahl}
                  onHaken={haken} onZiehen={setZieht}>
                  <button type="button" className={zeilenKlasse("pk-zeile", aktiv === z.id && ansicht === "karte", z)}
                    onClick={() => oeffnen(z.id)}>
                    <span className="pk-zeile-kopf">
                      <UngelesenPunkt zeile={z} />
                      <b data-pii>{z.email_direction === "sent" ? `An ${cleanSender(z.recipient) || "—"}` : cleanSender(z.sender) || z.sender_email || "—"}</b>
                      <span className="pk-zeit">{uhrzeit(z.received_at)}</span>
                    </span>
                    <span className="pk-zeile-betreff"><span data-pii>{z.subject || "(ohne Betreff)"}</span><Anhang da={z.has_attachments} /></span>
                    <span className="pk-leise" style={{ fontSize: 13 }}>{[z.imap_folder, z.status === "done" ? "erledigt" : null].filter(Boolean).join(" · ")}</span>
                  </button>
                </Waehlbar>
              ))}
              {trefferQ.data && trefferQ.data.items.length === 0 && <p className="pk-leise pk-topf-leer">Im Portal nichts gefunden.</p>}
              <PostfachTreffer tenant={tenant} postfach={pf.wahl || undefined} q={begriff} />
            </div>
          ) : (<>
          <div className="pk-stand">
            <p className="pk-stand-zahl">{q.isLoading ? "Lädt…" : n === 0 ? "Alles erledigt" : n === 1 ? "Noch 1 Mail für dich" : `Noch ${n} Mails für dich`}</p>
            {q.data && (
              <p className="pk-leise">
                {q.data.automatik_anzahl > 0 ? `${q.data.automatik_anzahl} hat der Agent heute nach Regeln erledigt` : "Heute noch nichts automatisch erledigt"}
                {q.data.erledigt.length > 0 ? ` · ${q.data.erledigt.length} von euch` : ""}
              </p>
            )}
            {q.isError && <p className="pk-fehler">Die Liste konnte nicht geladen werden. Bitte neu laden.</p>}
          </div>

          <Topf titel="Ein Klick" art="klick" zeilen={klick} aktiv={ansicht === "karte" ? aktiv : null} onWahl={oeffnen}
            leer="Alles mit einem Klick ist erledigt." wahl={wahl} />
          {/* Paul 28.10.2026: „Von Hand“ ist keine glückliche Formulierung → „Braucht dich“ */}
          <Topf titel="Braucht dich" art="hand" zeilen={hand} aktiv={ansicht === "karte" ? aktiv : null} onWahl={oeffnen}
            leer="Keine offenen Fragen." wahl={wahl} />
          <ZurKenntnis zeilen={q.data?.kenntnis || []} aktiv={ansicht === "karte" ? aktiv : null} onWahl={oeffnen}
            wahl={wahl} spamOrdner={(ordnerQ.data || []).find((f) => istSpamOrdner(f)) || null} sammelaktion={sammelaktion} />

          <button type="button" className={ansicht === "automatik" ? "pk-auto aktiv" : "pk-auto"}
            onClick={() => setAnsicht("automatik")} data-testid="automatik-pruefen">
            <span className="pk-auto-haken" aria-hidden><Check className="w-4 h-4" /></span>
            <span className="pk-auto-text">
              <b>{q.data?.automatik_anzahl ?? 0} automatisch erledigt</b>
              <span>{(q.data?.automatik.length ?? 0) === 1 ? "nach 1 freigegebenen Regel" : `nach ${q.data?.automatik.length ?? 0} freigegebenen Regeln`}</span>
            </span>
            <span className="pk-auto-pruefen">prüfen</span>
          </button>

          {(q.data?.erledigt.length ?? 0) > 0 && (
            <div className="pk-topf">
              <TopfKopf titel="Heute erledigt" anzahl={q.data!.erledigt.length} art="leise" zu={erledigtZu} onUmschalten={erledigtUmschalten} />
              {!erledigtZu && q.data!.erledigt.map((z) => (
                <button key={z.id} type="button" className={aktiv === z.id && ansicht === "karte" ? "pk-erledigt aktiv" : "pk-erledigt"}
                  onClick={() => oeffnen(z.id)}>
                  <Check className="w-4 h-4" aria-hidden />
                  <span data-pii>{cleanSender(z.absender)} · {z.betreff}</span>
                  <span className="pk-zeit">{uhrzeit(z.um)}</span>
                </button>
              ))}
            </div>
          )}
          </>)}
        </aside>

        <main className="pk-karte-spalte">
          {ansicht === "automatik" ? (
            <AutomatikPruefen gruppen={q.data?.automatik || []} offen={n}
              onZurueck={() => setAnsicht("karte")} onGeholt={() => qc.invalidateQueries({ queryKey: ["emails"] })} />
          ) : aktiv ? (
            <section className="pk-karte" aria-label="Mail">
              <EmailDetailPane
                key={aktiv}
                karte
                emailId={aktiv}
                ganzeMailZeigen={gewaehlt?.topf === "hand"}
                alsGelesen={sel === aktiv}
                onClose={naechste}
                onErledigt={naechste}
                onOpenEmail={ausVerlaufOeffnen}
                onTenantErkannt={(t) => { if (t && t !== tenant) setTenant(t) }}
              />
            </section>
          ) : liste ? (
            <div className="pk-fertig">
              <h2>{liste.titel}</h2>
              <p className="pk-leise">Links eine Mail wählen, sie öffnet sich hier als Karte.</p>
            </div>
          ) : q.data ? (
            <div className="pk-fertig">
              <span className="pk-fertig-haken" aria-hidden><Check className="w-8 h-8" /></span>
              <h2>Für heute ist alles erledigt.</h2>
              <p className="pk-leise">Neue Mails erscheinen hier, sobald sie ankommen.</p>
              <button type="button" className="pk-knopf-leise" onClick={() => setAnsicht("automatik")}>Automatisch Erledigtes prüfen</button>
            </div>
          ) : null}
        </main>
      </div>
      {composeOpen && (
        <ComposeModal open={composeOpen} onClose={() => setComposeOpen(false)} tenantSlug={tenant}
          postfachVorwahl={pf.wahl} onSent={() => qc.invalidateQueries({ queryKey: ["emails"] })} />
      )}
    </div>
  )
}

/** Ein Topf lässt sich zuklappen, damit man schnell zum nächsten kommt
 *  (Paul 28.10.2026). Je Browser gemerkt. */
export function useZugeklappt(art: string, standard = false): [boolean, () => void] {
  const key = `pk_topf_zu_${art}`
  const [zu, setZu] = useState(() => {
    try { const v = localStorage.getItem(key); return v == null ? standard : v === "1" } catch { return standard }
  })
  const umschalten = () => {
    setZu((alt) => {
      try { localStorage.setItem(key, alt ? "0" : "1") } catch { /* privat */ }
      return !alt
    })
  }
  return [zu, umschalten]
}

function TopfKopf({ titel, anzahl, art, zu, onUmschalten }: {
  titel: string; anzahl: number; art: string; zu: boolean; onUmschalten: () => void
}) {
  return (
    <h2 className={`pk-topf-titel ${art}`}>
      <button type="button" className="pk-topf-schalter" aria-expanded={!zu} onClick={onUmschalten}
        title={zu ? "Mails einblenden" : "Mails ausblenden"} data-testid={`schalter-${art}`}>
        <ChevronRight className={zu ? "w-4 h-4" : "w-4 h-4 pk-offen"} aria-hidden />
        <span>{titel}</span>
      </button>
      <span>{anzahl}</span>
    </h2>
  )
}

function Topf({ titel, art, zeilen, aktiv, onWahl, leer, wahl }: {
  titel: string; art: "klick" | "hand"; zeilen: ToepfeZeile[]; aktiv: number | null
  onWahl: (id: number) => void; leer: string
  wahl: { auswahl: number[]; onHaken: (id: number) => void; onZiehen: (an: boolean) => void }
}) {
  const [zu, umschalten] = useZugeklappt(art)
  return (
    <div className="pk-topf" data-testid={`topf-${art}`}>
      <TopfKopf titel={titel} anzahl={zeilen.length} art={art} zu={zu} onUmschalten={umschalten} />
      {!zu && zeilen.map((z) => (
        <Waehlbar key={z.id} id={z.id} ordner={z.imap_folder} gewaehlt={wahl.auswahl.includes(z.id)} auswahl={wahl.auswahl}
          onHaken={wahl.onHaken} onZiehen={wahl.onZiehen}>
          <button type="button" className={zeilenKlasse("pk-zeile", aktiv === z.id, z)} onClick={() => onWahl(z.id)}>
            <span className="pk-zeile-kopf">
              <UngelesenPunkt zeile={z} />
              <Absender zeile={z} />
              <span className="pk-zeit">{uhrzeit(z.received_at)}</span>
            </span>
            <span className="pk-zeile-betreff"><span data-pii>{z.subject || "(ohne Betreff)"}</span><Anhang da={z.has_attachments} /></span>
            {z.vorschau && <span className="pk-zeile-vorschau" data-pii>{z.vorschau}</span>}
            <span className={`pk-zeile-kurz ${art}`}>{z.kurz}</span>
          </button>
        </Waehlbar>
      ))}
      {!zu && zeilen.length === 0 && <p className="pk-leise pk-topf-leer">{leer}</p>}
    </div>
  )
}

/** „Zur Kenntnis“ (Paul 28.10.2026): der Rest des Posteingangs, ohne To-do —
 *  Info, Werbung, Phishing, ohne Plan. Damit steht jede Mail des Posteingangs
 *  irgendwo. Werbung und Phishing gehen in den Spam-Ordner, der Rest wird
 *  erledigt (bleibt im Postfach). Standard: zugeklappt. */
function ZurKenntnis({ zeilen, aktiv, onWahl, wahl, spamOrdner, sammelaktion }: {
  zeilen: KenntnisZeile[]; aktiv: number | null; onWahl: (id: number) => void
  wahl: { auswahl: number[]; onHaken: (id: number) => void; onZiehen: (an: boolean) => void }
  spamOrdner: string | null
  sammelaktion: (ids: number[], action: "move" | "done", folder?: string) => Promise<void>
}) {
  const [zu, umschalten] = useZugeklappt("kenntnis", true)
  const spam = (z: KenntnisZeile) => !!spamOrdner && (z.art === "werbung" || z.art === "phishing")
  const alleAblegen = async () => {
    const insSpam = zeilen.filter(spam).map((z) => z.id)
    const erledigt = zeilen.filter((z) => !spam(z)).map((z) => z.id)
    if (insSpam.length && spamOrdner) await sammelaktion(insSpam, "move", spamOrdner)
    if (erledigt.length) await sammelaktion(erledigt, "done")
  }
  return (
    <div className="pk-topf pk-kenntnis" data-testid="topf-kenntnis">
      <TopfKopf titel="Zur Kenntnis" anzahl={zeilen.length} art="kenntnis" zu={zu} onUmschalten={umschalten} />
      <p className="pk-leise pk-kenntnis-hinweis">Kein To-do. Der Agent hat nichts zu tun gefunden, keine Regel hat sie weggeräumt.</p>
      {!zu && (
        <div className="pk-kenntnis-liste">
          {zeilen.map((z) => (
            <Waehlbar key={z.id} id={z.id} ordner={z.imap_folder} gewaehlt={wahl.auswahl.includes(z.id)} auswahl={wahl.auswahl}
              onHaken={wahl.onHaken} onZiehen={wahl.onZiehen}>
              <div className={zeilenKlasse("pk-kenntnis-zeile", aktiv === z.id, z)}>
                <button type="button" className="pk-kenntnis-text" onClick={() => onWahl(z.id)}>
                  <span className="pk-zeile-kopf">
                    <UngelesenPunkt zeile={z} />
                    <Absender zeile={z} />
                    <span className="pk-zeit">{fmtZeitpunkt(z.received_at)}</span>
                  </span>
                  <span className="pk-zeile-betreff"><span data-pii>{z.subject || "(ohne Betreff)"}</span><Anhang da={z.has_attachments} /></span>
                  {z.vorschau && <span className="pk-zeile-vorschau" data-pii>{z.vorschau}</span>}
                  <span className={z.art === "phishing" ? "pk-kenntnis-grund rot" : "pk-kenntnis-grund"}>{z.grund}</span>
                </button>
                {/* Paul 28.10.2026: „Erledigt“ als kleiner Haken, damit es schnell geht */}
                <button type="button" className={spam(z) ? "pk-kenntnis-ab spam" : "pk-kenntnis-ab"}
                  title={spam(z) ? `In Spam („${spamOrdner}“)` : "Erledigt (bleibt im Postfach)"}
                  aria-label={spam(z) ? "In Spam verschieben" : "Erledigt"}
                  onClick={() => spam(z) ? sammelaktion([z.id], "move", spamOrdner!) : sammelaktion([z.id], "done")}>
                  {spam(z) ? <ShieldAlert className="w-4 h-4" aria-hidden /> : <Check className="w-4 h-4" aria-hidden />}
                </button>
              </div>
            </Waehlbar>
          ))}
          {zeilen.length > 0 && (
            <button type="button" className="pk-kenntnis-alle" onClick={alleAblegen} data-testid="kenntnis-alle">
              Alle {zeilen.length} ablegen
            </button>
          )}
          {zeilen.length === 0 && <p className="pk-leise pk-topf-leer">Nichts weiter im Posteingang.</p>}
        </div>
      )}
    </div>
  )
}

/** „Automatisch erledigt“ prüfen: je Regel, was sie getan hat, und jede Mail
 *  lässt sich zurück in den Posteingang holen. Nichts ist gelöscht. */
function AutomatikPruefen({ gruppen, offen, onZurueck, onGeholt }: {
  gruppen: AutomatikGruppe[]; offen: number; onZurueck: () => void; onGeholt: () => void
}) {
  const [geholt, setGeholt] = useState<Set<number>>(new Set())
  const [laeuft, setLaeuft] = useState<number | null>(null)
  const zurueckholen = async (id: number, betreff: string) => {
    setLaeuft(id)
    try {
      const r = await fetch(`/api/v1/emails/${id}/move`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ folder: "INBOX" }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`)
      setGeholt((alt) => new Set(alt).add(id))
      toast(`„${betreff}“ liegt wieder im Posteingang`, "success")
      onGeholt()
    } catch (err) {
      toast(`Zurückholen fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`, "error")
    } finally {
      setLaeuft(null)
    }
  }
  return (
    <section className="pk-karte pk-auto-ansicht" aria-label="Automatisch erledigt">
      <div className="pk-auto-kopf">
        <div>
          <h2>Heute automatisch erledigt</h2>
          <p className="pk-leise">Nichts davon ist gelöscht. Alles liegt im Postfach, nur nicht mehr im Posteingang.</p>
        </div>
        <button type="button" className="pk-knopf-leise" onClick={onZurueck}>
          {offen ? `Zurück zu deinen ${offen}` : "Zurück"}
        </button>
      </div>
      {gruppen.length === 0 && <p className="pk-leise">Heute hat noch keine Regel etwas weggeräumt.</p>}
      {gruppen.map((g) => (
        <div key={g.regel} className="pk-regel">
          <div className="pk-regel-kopf">
            <h3>{g.label}</h3>
            <span className="pk-regel-zahl">{g.anzahl}</span>
          </div>
          <p className="pk-leise">{g.beschreibung}{g.ziel ? ` Ziel: ${g.ziel}.` : ""}
            {g.rueckgeholt > 0 ? ` ${g.rueckgeholt} zurückgeholt.` : ""}</p>
          <div className="pk-regel-liste">
            {g.mails.map((m) => {
              const zurueck = m.rueckgeholt || geholt.has(m.id)
              return (
                <div key={m.id} className="pk-regel-zeile">
                  <b data-pii>{cleanSender(m.absender)}</b>
                  <span data-pii>{m.betreff || "(ohne Betreff)"}</span>
                  <button type="button" className={zurueck ? "pk-knopf-klein fertig" : "pk-knopf-klein"}
                    disabled={zurueck || laeuft === m.id} onClick={() => zurueckholen(m.id, m.betreff)}>
                    {zurueck ? "Zurückgeholt" : laeuft === m.id ? "…" : "Zurückholen"}
                  </button>
                </div>
              )
            })}
          </div>
          {g.anzahl > g.mails.length && <p className="pk-leise">und {g.anzahl - g.mails.length} weitere</p>}
        </div>
      ))}
      <p className="pk-leise">Zurückgeholte Mails landen wieder unter „Braucht dich“. Holt das Büro bei einer Regel zweimal
        in sieben Tagen etwas zurück, geht sie von selbst wieder auf Probe.</p>
    </section>
  )
}
